import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Accordion } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import BootcampHero from "@/components/bootcamp/BootcampHero";
import EverydayEssentials from "@/components/bootcamp/EverydayEssentials";
import BootcampDay from "@/components/bootcamp/BootcampDay";
import BusinessMilestones from "@/components/milestones/BusinessMilestones";
import BootcampResources from "@/components/bootcamp/BootcampResources";
import { titleCase } from "@/components/bootcamp/format";

export default function Bootcamp() {
  const { isReadOnly, isViewAs, user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("days");
  const [open, setOpen] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    api.get("/bootcamp").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the bootcamp"));
  }, []);

  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading bootcamp…</div>;
  const { content, progress } = data;

  const toggle = async (kind, key, completed) => {
    setBusyKey(`${kind}-${key}`);
    try {
      const r = await api.post(`/bootcamp/${kind}/${key}`, { completed });
      setData((d) => ({ ...d, progress: r.data }));
      toast.success(completed ? `Day ${key} complete` : `Day ${key} reopened`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save progress");
    } finally {
      setBusyKey(null);
    }
  };

  const goToDay = (d) => {
    setTab("days");
    setOpen((o) => (o.includes(`day-${d}`) ? o : [...o, `day-${d}`]));
    setTimeout(() => document.getElementById(`bootcamp-day-${d}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  };

  return (
    <div className="space-y-6" data-testid="bootcamp-page">
      <BootcampHero content={content} progress={progress} onContinue={() => goToDay(progress.next_day ?? content.total_days)} />
      <EverydayEssentials content={content} />
      {isReadOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-[#C9A961]/40 bg-[#F7F5EF] px-3 py-2 text-xs text-[#0A1F44]" data-testid="bootcamp-readonly-note">
          <Eye className="w-4 h-4 text-[#C9A961]" />
          {isViewAs ? `Read-only preview of ${user?.name}'s bootcamp progress.` : "Read-only access — progress can't be changed from this account."}
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 rounded-full p-1 h-auto flex w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="days" data-testid="bootcamp-tab-days" className="rounded-full data-[state=active]:bg-[#0A1F44] data-[state=active]:text-white text-xs sm:text-sm">Daily training</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="bootcamp-tab-milestones" className="rounded-full data-[state=active]:bg-[#0A1F44] data-[state=active]:text-white text-xs sm:text-sm">Milestones</TabsTrigger>
          <TabsTrigger value="resources" data-testid="bootcamp-tab-resources" className="rounded-full data-[state=active]:bg-[#0A1F44] data-[state=active]:text-white text-xs sm:text-sm">Valuable Resources</TabsTrigger>
        </TabsList>

        <TabsContent value="days" className="mt-5 space-y-6">
          <Accordion type="multiple" value={open} onValueChange={setOpen}>
            {content.phases.map((p) => {
              const done = p.days.filter((d) => progress.days[String(d.day)]).length;
              return (
                <section key={p.number} className="mb-6" data-testid={`bootcamp-phase-${p.number}`}>
                  <div className="flex items-end justify-between gap-3 mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Phase {p.number}</div>
                      <h2 className="font-serif text-lg text-[#0A1F44]">{titleCase(p.title)}</h2>
                    </div>
                    <span className="text-xs text-slate-500 tabular-nums" data-testid={`bootcamp-phase-progress-${p.number}`}>{done}/{p.days.length}</span>
                  </div>
                  {p.days.map((d) => (
                    <BootcampDay key={d.day} day={d} doneAt={progress.days[String(d.day)]} readOnly={isReadOnly}
                                 busy={busyKey === `days-${d.day}`} onToggle={(day, c) => toggle("days", day, c)}
                                 onOpenResources={() => { setTab("resources"); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
                  ))}
                </section>
              );
            })}
          </Accordion>
        </TabsContent>

        <TabsContent value="milestones" className="mt-5">
          <BusinessMilestones userId={user.id} />
        </TabsContent>

        <TabsContent value="resources" className="mt-5">
          <BootcampResources data={content.valuable_resources} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
